import { notification } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMarketInventoryGetThunk } from "tm-shared/api";
import { TaskRejectProducts } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import Header from "@/components/Header";
import { Loading } from "@/components/Loading";
import Meta from "@/components/Meta";
import { SVScanButton } from "@/components/SVScanButton";
import { HeaderFilterButton } from "@/components/UI/HeaderFilterButton";
import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useQueryFilter } from "@/hooks/useQueryFilter";
import { ConfirmActionBtn } from "@/pages/WorkPlan/Tasks/components/ConfirmActionBtn";

import { RejectFilterDrawer } from "./components/RejectFilterDrawer";
import { RejectProductsList } from "./components/RejectProductsList";
import { RejectProductsTopSection } from "./components/RejectProductsTopSection";
import { useTaskCompletion } from "./hooks";
import { StyledMainContainer } from "./RejectProducts.styled";
import { StrictRejectProduct } from "./RejectProducts.types";

interface RejectProductsPageProps {
    marketId: string;
    task: TaskRejectProducts;
}

export const RejectProductsPage: FC<RejectProductsPageProps> = ({
    marketId,
    task,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { rejectProducts } = useAppSelector((state) => state.progress);
    const { queryMap, selectedFilters } = useAppSelector(
        (state) => state.ui.rejectProducts,
    );

    const {
        offlineMode,
        products,
        marketsPriceLists,
        priceLists,
        marketsInventories,
    } = useAppSelector((state) => state.offline);

    const [open, setOpen] = useState(false);

    const taskId = task.id;

    const currentInventory = marketsInventories?.[marketId] ?? {};
    const currentPriceList = priceLists?.[marketsPriceLists?.[marketId]] ?? {};
    const rejectProductsList = rejectProducts[taskId] ?? {};

    const inventoryCount = useMemo(
        () => Object.keys(currentInventory ?? {}).length,
        [currentInventory],
    );

    const filterCount = useMemo(
        () => Object.values(selectedFilters || {}).length,
        [selectedFilters],
    );

    const initialRejectProducts: Record<string, StrictRejectProduct> = useMemo(
        () =>
            Object.keys(currentInventory || {}).reduce(
                (acc, key) => {
                    const product = products[key.trim()];

                    if (product) {
                        acc[key] = {
                            ...product,
                            price: currentPriceList?.[key]?.price,
                            max: currentInventory[key],
                        };
                    }
                    return acc;
                },
                {} as Record<string, StrictRejectProduct>,
            ),
        [currentInventory, currentPriceList, products],
    );

    const [filteredProducts, setFilteredProducts] = useState<
        StrictRejectProduct[]
    >(() => Object.values(initialRejectProducts));

    const searchQuery = useMemo(
        () => queryMap?.[taskId] ?? "",
        [queryMap, taskId],
    );

    const searchFilteredProducts = useQueryFilter<StrictRejectProduct>(
        filteredProducts,
        searchQuery,
        [(p) => p?.name, (p) => p?.barcode],
    );

    const productsListLength = searchFilteredProducts.length;

    const handleConfirmUpdateRemaining = useCallback(() => {
        navigate(
            buildRoutePath(ROUTES.UpdateProductBalancesTask, {
                taskId,
                marketId,
            }),
        );
    }, [marketId, taskId, navigate]);

    const openDrawer = useCallback(() => setOpen(true), []);

    useEffect(() => {
        if (!offlineMode) {
            dispatch(getMarketInventoryGetThunk({ marketId }));
        }
    }, [dispatch, marketId, offlineMode]);

    const { loading, handleConfirm: handleConfirmTask } = useTaskCompletion({
        rejectProductsList,
        task,
        marketId,
    });

    const onFilterClick = () =>
        productsListLength > 0
            ? openDrawer()
            : notification.error({
                  message:
                      "Немає товарів для повернення, щоб їх відфільтрувати",
              });

    return (
        <>
            <Meta title="Повернення товару" />

            <Header
                title="Повернення товару"
                back
                rightActions={
                    <>
                        <SVScanButton marketId={marketId} taskId={taskId} />
                        <HeaderFilterButton
                            onClick={onFilterClick}
                            filterCount={filterCount}
                        />
                    </>
                }
            />

            <RejectProductsTopSection
                rejectProductsList={rejectProductsList}
                taskId={taskId}
                handleConfirmUpdateRemaining={handleConfirmUpdateRemaining}
            />

            <StyledMainContainer className="reject-product-list">
                <RejectProductsList
                    products={searchFilteredProducts}
                    taskId={taskId}
                    hasProducts={inventoryCount > 0}
                    rejectProductsList={rejectProductsList}
                />

                {productsListLength > 0 && (
                    <ConfirmActionBtn
                        onConfirm={handleConfirmTask}
                        title="Завершити задачу"
                        description="Ви впевнені що хочете завершити виконання задачі?"
                        loading={loading}
                    />
                )}
            </StyledMainContainer>

            <RejectFilterDrawer
                open={open}
                setOpen={setOpen}
                setFilteredProducts={setFilteredProducts}
                inventoryList={currentInventory}
                products={initialRejectProducts}
            />

            {loading && <Loading />}
        </>
    );
};
